import React, { useState } from 'react';
import styled from 'styled-components';
import Layout from '../../components/Layout';
import Card from '../../components/Card';

const PageTitle = styled.h2`
  font-size: 28px;
  font-weight: 600;
  color: #1A2440;
  margin-bottom: 20px;
`;

const TabsContainer = styled.div`
  display: flex;
  margin-bottom: 20px;
  border-bottom: 1px solid #e9ecef;
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 12px 20px;
  background: none;
  border: none;
  border-bottom: 3px solid ${props => props.active ? '#1A2440' : 'transparent'};
  color: ${props => props.active ? '#1A2440' : '#6c757d'};
  font-size: 16px;
  font-weight: ${props => props.active ? '600' : '400'};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    color: #1A2440;
  }
`;

const GenerateButton = styled.button`
  background-color: #4267B2;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 12px 20px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: #365899;
  }
`;

const QuestionCard = styled.div`
  background-color: #ffffff;
  border-radius: 8px;
  padding: 15px 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Question = styled.div`
  font-size: 16px;
  color: #1A2440;
`;

const AddButton = styled.button`
  background-color: #ffffff;
  color: #1A2440;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 14px;
  cursor: pointer;
  
  &:hover {
    background-color: #f8f9fa;
  }
`;

const ResponseCard = styled.div`
  background-color: #ffffff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 20px;
`;

const ResponseQuestion = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1A2440;
  margin-top: 0;
  margin-bottom: 10px;
`;

const ResponseAnswer = styled.p`
  font-size: 14px;
  color: #6c757d;
  line-height: 1.6;
  margin-bottom: 0;
`;

const SectionTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #1A2440;
  margin-bottom: 15px;
`;

// Sample suggested questions
const suggestedQuestions = [
  "What is the company's growth strategy?",
  "Can you provide more details on the customer base?",
  "How does the company differentiate itself from competitors?",
];

// Sample management responses
const managementResponses = [
  {
    id: 1,
    question: "What are the key drivers of recent revenue growth?",
    answer: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed d do eiusmod tempor incididunt ut labore",
  },
  {
    id: 2,
    question: "Describe the current sales pipeline and outlook.",
    answer: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed d do eiusmod tempor incididunt ut labore",
  },
  {
    id: 3,
    question: "What are the biggest operational challenges?",
    answer: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed d do eiusmod tempor incididunt ut labore",
  },
];

const QAPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('questions');
  const [userQuestions, setUserQuestions] = useState<string[]>([]);

  const handleAddQuestion = (question: string) => {
    if (!userQuestions.includes(question)) {
      setUserQuestions([...userQuestions, question]);
    }
  };

  return (
    <Layout 
      companyName="Sample Company"
      sector="Software"
      location="United States"
    >
      <PageTitle>Q&A</PageTitle>
      
      <TabsContainer>
        <Tab 
          active={activeTab === 'questions'} 
          onClick={() => setActiveTab('questions')}
        >
          Questions
        </Tab>
        <Tab 
          active={activeTab === 'feedback'} 
          onClick={() => setActiveTab('feedback')}
        >
          Feedback
        </Tab>
      </TabsContainer>
      
      {activeTab === 'questions' && (
        <>
          <GenerateButton>
            Generate Suggested Questions
          </GenerateButton>
          
          <SectionTitle>Suggested Questions</SectionTitle>
          {suggestedQuestions.map((question, index) => (
            <QuestionCard key={index}>
              <Question>{question}</Question>
              <AddButton onClick={() => handleAddQuestion(question)}>
                Add to List
              </AddButton>
            </QuestionCard>
          ))}
          
          {userQuestions.length > 0 && (
            <>
              <SectionTitle>My Questions</SectionTitle>
              {userQuestions.map((question, index) => (
                <QuestionCard key={index}>
                  <Question>{question}</Question>
                </QuestionCard>
              ))}
            </>
          )}
          
          <SectionTitle>Management Responses</SectionTitle>
          {managementResponses.map(response => (
            <ResponseCard key={response.id}>
              <ResponseQuestion>{response.question}</ResponseQuestion>
              <ResponseAnswer>{response.answer}</ResponseAnswer>
            </ResponseCard>
          ))}
        </>
      )}
      
      {activeTab === 'feedback' && (
        <Card title="Feedback">
          <p>This section will contain feedback from management and other stakeholders.</p>
        </Card>
      )}
    </Layout>
  );
};

export default QAPage;
